"use client";
// src/components/RecentRecords/RecentRecords.jsx
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Empty,
  Dropdown,
  Checkbox,
  Tooltip,
  Badge,
  Popover,
  message,
  Spin,
} from "antd";
import {
  FileImageOutlined,
  FilterOutlined,
  CalendarOutlined,
  SortAscendingOutlined,
  SendOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  LoadingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./RecentRecords.css";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectEffectiveUserId } from "../../../sous-profile/redux/selectors/sousProfileSelectors";
import ShareModal from "../ShareModal/ShareModal";
import { resolveDoctorAvatar } from "../../../ProfileDoctor/hooks/useDoctorInformation";
import { getSharedDocsStorageKey } from "../../utils/storageKeyUtils";

// Sharing thunks & actions
import { revokeDocumentAccess } from "../../redux/thunks/sharingthunks";
import { removeAccessEntryLocal } from "../../redux/slices/sharingslice";

// ─────────────────────────────────────────────
// RecentRecords
// ─────────────────────────────────────────────
export default function RecentRecords({
  records = [],
  loading = false,
  onFindLab,
  title,
  subtitle,
  bannerTextLeft,
  bannerCta,
  onFilterChange,
  onSelect,
  selectedId,
  onShare,
  onDelete,
  onDownload,
  onShareSuccess,
  // Callback appelé après désactivation réussie d'un accès médecin.
  // Signature : (documentId, doctorId) => void
  onDoctorAccessRevoked,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUserId = useSelector(selectEffectiveUserId);

  // Permet de surcharger l'id patient via props si besoin (ex: page médecin)
  const patientId = typeof records?.[0]?.patientId !== 'undefined' ? records[0].patientId : currentUserId;

  const [activeFilter, setActiveFilter] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [documentsToShare, setDocumentsToShare] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Map documentId -> Set(doctorId) en cours de révocation
  const [revokingMap, setRevokingMap] = useState({});
  // Track doctors added locally after sharing (before Redux refresh)
  const [sharedDoctorsMap, setSharedDoctorsMap] = useState({});

  // ─── Hydrate sharedDoctorsMap depuis localStorage à la connexion ───
  const hydrateFromStorage = useCallback(() => {
    if (!patientId) return;
    const storageKey = getSharedDocsStorageKey(patientId);
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "[]");
      if (!Array.isArray(saved) || saved.length === 0) return;
      const map = {};
      saved.forEach((doc) => {
        const key = String(doc.id || doc._id || "");
        if (!key || !Array.isArray(doc.sharedDoctors) || doc.sharedDoctors.length === 0) return;
        map[key] = doc.sharedDoctors;
      });
      if (Object.keys(map).length > 0) {
        setSharedDoctorsMap(map);
      }
    } catch {
      // ignore parse errors
    }
  }, [patientId]);

  useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  // Écoute les mises à jour du localStorage (après partage) pour rester en sync
  useEffect(() => {
    const handleStorageUpdate = (e) => {
      if (e?.detail?.patientId && String(e.detail.patientId) === String(patientId)) {
        hydrateFromStorage();
      }
    };
    window.addEventListener("shared-medical-docs-updated", handleStorageUpdate);
    return () => window.removeEventListener("shared-medical-docs-updated", handleStorageUpdate);
  }, [patientId, hydrateFromStorage]);

  const handleFilterClick = (filterType) => {
    const newFilter = activeFilter === filterType ? null : filterType;
    setActiveFilter(newFilter);
    if (onFilterChange) onFilterChange(newFilter);
  };

  const toggleSelectionMode = () => {
    if (selectionMode) setSelectedIds([]);
    setSelectionMode(!selectionMode);
  };

  const handleSelectAll = () => {
    const allIds = filteredRecords.map((r) => String(r.id || r._id));
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const handleCheckboxChange = (id, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, String(id)]);
    } else {
      setSelectedIds((prev) => prev.filter((i) => i !== String(id)));
    }
  };

  const handleShareSelected = () => {
    if (selectedIds.length > 0) {
      const selectedRecords = records
        .filter((r) => selectedIds.includes(String(r.id || r._id)))
        .map((r) => {
          // Remove non-serializable properties before passing to ShareModal
          const { onClick, ...cleanRecord } = r;
          return cleanRecord;
        });
      setDocumentsToShare(selectedRecords);
      setShareModalVisible(true);
    }
  };

  const handleShareSuccess = (sharedItems, doctors, settings) => {
    // Merge newly shared doctors into local map for immediate avatar display
    setSharedDoctorsMap((prev) => {
      const next = { ...prev };
      documentsToShare.forEach((doc) => {
        const docId = String(doc.id || doc._id);
        const existing = Array.isArray(next[docId]) ? next[docId] : [];
        const existingIds = new Set(existing.map((d) => String(d?.id || "")));
        const newDoctors = (doctors || [])
          .map((d) => ({
            id: d?.id || d?._id || d?.userId || d?.doctorId || d?.medecinId,
            name:
              d?.fullName ||
              d?.name ||
              `${d?.firstName || ""} ${d?.lastName || ""}`.trim() ||
              "Doctor",
            specialty: d?.specialty || d?.role || "",
            avatarUrl: d?.avatarUrl || d?.avatar || d?.photo || null,
            isActive:
              !settings?.expiresAt ||
              new Date(settings.expiresAt) > new Date(),
            isFromRendezvous: true,
          }))
          .filter((d) => d.id && !existingIds.has(String(d.id)));
        next[docId] = [...existing, ...newDoctors];
      });
      return next;
    });

    setSelectedIds([]);
    setSelectionMode(false);
    setShareModalVisible(false);
    if (onShare) onShare(sharedItems, doctors);
    if (onShareSuccess) onShareSuccess(sharedItems, doctors);
  };

  const handleDeleteSelected = () => {
    if (onDelete && selectedIds.length > 0) {
      const selectedRecords = records.filter((r) =>
        selectedIds.includes(String(r.id || r._id))
      );
      onDelete(selectedRecords);
      setSelectedIds([]);
      setSelectionMode(false);
    }
  };

  const handleDownloadSelected = () => {
    if (onDownload && selectedIds.length > 0) {
      const selectedRecords = records.filter((r) =>
        selectedIds.includes(String(r.id || r._id))
      );
      onDownload(selectedRecords);
    }
  };

  // ─── Désactiver (révoquer) l'accès d'un médecin à un document ───
  /**
   * 1. Mise à jour optimiste Redux locale
   * 2. Appel API DELETE /extraction/sharing/:documentId/access
   * 3. Notifie le parent via onDoctorAccessRevoked(documentId, doctorId)
   *    → PatientProfileCard écoute cet event pour retirer le doc de sa liste
   */
  const handleDeactivateDoctorShare = useCallback(
    async (recordId, doctorId) => {
      if (!recordId || !doctorId) return;

      const docId = String(recordId);
      const dId = String(doctorId);

      // Retire immédiatement du map local aussi
      setSharedDoctorsMap((prev) => {
        if (!prev[docId]) return prev;
        return {
          ...prev,
          [docId]: prev[docId].filter((d) => String(d?.id || "") !== dId),
        };
      });

      // Marque comme "en cours"
      setRevokingMap((prev) => {
        const docSet = new Set(prev[docId] || []);
        docSet.add(dId);
        return { ...prev, [docId]: docSet };
      });

      // Mise à jour optimiste locale Redux
      dispatch(removeAccessEntryLocal({ documentId: docId, doctorId: dId }));

      // ─── Mise à jour optimiste localStorage ───────────────────────────
      // Supprime le document ENTIER (pas seulement le médecin) pour qu'il
      // disparaisse de PatientProfileCard même après rechargement de page.
      if (patientId) {
        try {
          const storageKey = getSharedDocsStorageKey(patientId);
          const existing = JSON.parse(localStorage.getItem(storageKey) || "[]");
          const updated = existing.filter((doc) => {
            const docKey = String(
              doc.id || doc._id || doc.href || doc.fileUrl || doc.pdfUrl || doc.title
            );
            return docKey !== docId;
          });
          localStorage.setItem(storageKey, JSON.stringify(updated));

          // Persiste l'ID révoqué dans un set dédié pour survivre aux recharges
          const revokedKey = `revoked_docs_${patientId}`;
          let revokedIds = [];
          try { revokedIds = JSON.parse(localStorage.getItem(revokedKey) || "[]"); } catch { revokedIds = []; }
          if (!revokedIds.includes(docId)) {
            revokedIds.push(docId);
            localStorage.setItem(revokedKey, JSON.stringify(revokedIds));
          }

          // Notifie PatientProfileCard immédiatement (optimiste)
          window.dispatchEvent(
            new CustomEvent("doctor-access-revoked", {
              detail: { documentId: docId, doctorId: dId, patientId: patientId },
            })
          );
          window.dispatchEvent(
            new CustomEvent("shared-medical-docs-updated", {
              detail: { patientId: patientId, docs: updated },
            })
          );
        } catch {
        }
      }

      // Notifie le parent prop (optimiste aussi)
      if (onDoctorAccessRevoked) {
        onDoctorAccessRevoked(docId, dId);
      }

      // Appel API
      try {
        await dispatch(
          revokeDocumentAccess({ documentId: docId, doctorIds: [dId] })
        ).unwrap();

        message.success(
          t("recentRecords.deactivateSuccess", "Accès révoqué avec succès")
        );

        // Notifie le parent prop
        if (onDoctorAccessRevoked) {
          onDoctorAccessRevoked(docId, dId);
        }
      } catch {
        message.error(
          t("recentRecords.deactivateError", "Erreur lors de la révocation")
        );
        // Rollback: re-fetch ou laisser Redux gérer
      } finally {
        setRevokingMap((prev) => {
          const docSet = new Set(prev[docId] || []);
          docSet.delete(dId);
          return { ...prev, [docId]: docSet };
        });
      }
    },
    [dispatch, patientId, onDoctorAccessRevoked, t]
  );

  const filterItems = [
    {
      key: "date",
      label: t("recentRecords.filterByDate"),
      icon: <CalendarOutlined />,
      onClick: () => handleFilterClick("date"),
    },
    {
      key: "name",
      label: t("recentRecords.filterByName"),
      icon: <SortAscendingOutlined />,
      onClick: () => handleFilterClick("name"),
    },
  ];

  const filteredRecords = records.filter(
    (r) =>
      r.title && r.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allFilteredSelected =
    filteredRecords.length > 0 &&
    filteredRecords.every((r) => selectedIds.includes(String(r.id || r._id)));

  // Merge locally tracked sharedDoctors (added after sharing) into records
  const recordsForRender = filteredRecords.map((r) => {
    const docId = String(r.id || r._id);
    const localDoctors = sharedDoctorsMap[docId];
    if (!localDoctors || localDoctors.length === 0) return r;
    const existing = Array.isArray(r.sharedDoctors) ? r.sharedDoctors : [];
    const existingIds = new Set(existing.map((d) => String(d?.id || "")));
    const toAdd = localDoctors.filter((d) => !existingIds.has(String(d?.id || "")));
    return toAdd.length === 0 ? r : { ...r, sharedDoctors: [...existing, ...toAdd] };
  });

  return (
    <section className="rr-wrapper">
      <div className="rr-header">
        <h2 className="rr1-title">{title || t("recentRecords.title")}</h2>

        <div className="rr-header-actions">
          {selectionMode && (
            <Tooltip
              title={
                allFilteredSelected
                  ? t("recentRecords.deselectAll", "Deselect all")
                  : t("recentRecords.selectAll", "Select all")
              }
            >
              <button
                className={`rr-filter-btn ${allFilteredSelected ? "rr-filter-active" : ""}`}
                onClick={handleSelectAll}
                type="button"
              >
                <CheckSquareOutlined />
                <span className="rr-select-all-label">
                  {allFilteredSelected
                    ? t("recentRecords.deselectAll", "Deselect all")
                    : t("recentRecords.selectAll", "Select all")}
                </span>
              </button>
            </Tooltip>
          )}

          <Tooltip
            title={
              selectionMode
                ? t("recentRecords.cancelSelection")
                : t("recentRecords.selectMultiple")
            }
          >
            <button
              className={`rr-filter-btn ${selectionMode ? "rr-filter-active" : ""}`}
              onClick={toggleSelectionMode}
              type="button"
            >
              {selectionMode ? <CloseOutlined /> : <CheckSquareOutlined />}
            </button>
          </Tooltip>

          <Dropdown
            menu={{
              items: filterItems,
              selectedKeys: activeFilter ? [activeFilter] : [],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <button
              className={`rr-filter-btn ${activeFilter ? "rr-filter-active" : ""}`}
              type="button"
            >
              <FilterOutlined />
            </button>
          </Dropdown>

          <Button
            type="primary"
            className="rr-banner-btn"
            shape="round"
            size="middle"
            onClick={() => navigate("/lab")}
          >
            {bannerCta || t("recentRecords.findLab")}
          </Button>
        </div>
      </div>

      {activeFilter === "name" && (
        <div className="rr-search-bar rr-search-bar--icon">
          <input
            type="text"
            placeholder={t("recentRecords.searchByName") || "Search by name"}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rr-search-input rr-search-input--icon"
          />
        </div>
      )}

      {selectionMode && selectedIds.length > 0 && (
        <div className="rr-actions-bar">
          <Badge count={selectedIds.length} size="small">
            <span className="rr-selected-count">
              {t("recentRecords.selectedCount", { count: selectedIds.length })}
            </span>
          </Badge>
          <div className="rr-actions-buttons">
            <Tooltip title={t("recentRecords.shareSelected")}>
              <Button
                className="rr-action-btn"
                icon={<SendOutlined />}
                onClick={handleShareSelected}
                disabled={selectedIds.length === 0}
              >
                {t("recentRecords.share")}
              </Button>
            </Tooltip>
          </div>
        </div>
      )}

      <div className="rr-subtitle">
        {subtitle || t("recentRecords.subtitle")}
      </div>

      <div className="rr-list">
        {loading ? (
          <>
            <RecordSkeleton />
            <RecordSkeleton />
            <RecordSkeleton />
          </>
        ) : recordsForRender.length === 0 ? (
          <Empty
            description={t("recentRecords.noRecords")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: "40px 0" }}
          >
            <Button type="primary">
              {t("recentRecords.downloadDocument")}
            </Button>
          </Empty>
        ) : (
          recordsForRender.map((r) => (
            <RecordItem
              key={r.id || r._id}
              id={String(r.id || r._id)}
              dateLabel={r.dateLabel}
              title={r.title}
              sharedDoctors={r.sharedDoctors}
              hasExpiration={r.hasExpiration}
              expiresAt={r.expiresAt}
              pdfUrl={r.pdfUrl || r.fileUrl || r.url}
              onClick={r.onClick}
              onSelect={onSelect}
              isSelected={selectedId === (r.id || r._id)}
              selectionMode={selectionMode}
              isChecked={selectedIds.includes(String(r.id || r._id))}
              onCheckChange={handleCheckboxChange}
              onDeactivateDoctorShare={handleDeactivateDoctorShare}
              revokingDoctors={revokingMap[String(r.id || r._id)] || new Set()}
            />
          ))
        )}
      </div>

      <ShareModal
        visible={shareModalVisible}
        onClose={() => setShareModalVisible(false)}
        documents={documentsToShare}
        onShareSuccess={handleShareSuccess}
        patientId={patientId}
      />
    </section>
  );
}

// ─────────────────────────────────────────────
// RecordItem
// ─────────────────────────────────────────────
function RecordItem({
  id,
  dateLabel,
  title,
  sharedDoctors,
  hasExpiration,
  expiresAt,
  pdfUrl,
  onClick,
  onSelect,
  isSelected,
  selectionMode,
  isChecked,
  onCheckChange,
  onDeactivateDoctorShare,
  revokingDoctors = new Set(),
}) {
  const { t } = useTranslation();

  const handleOpen = () => {
    if (onClick) return onClick();
    if (pdfUrl) window.open(pdfUrl, "_blank");
  };

  const handleSelect = () => {
    if (selectionMode) {
      onCheckChange(id, !isChecked);
    } else if (onSelect) {
      onSelect(id);
    } else {
      handleOpen();
    }
  };

  // Seuls les médecins "isFromRendezvous" sont affichés
  const visibleSharedDoctors = Array.isArray(sharedDoctors)
    ? sharedDoctors.filter((d) => d?.isFromRendezvous !== false)
    : [];

  return (
    <div
      className={`rr-item ${isSelected ? "rr-item-selected" : ""} ${
        isChecked ? "rr-item-checked" : ""
      }`}
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleSelect();
      }}
    >
      {selectionMode && (
        <Checkbox
          checked={isChecked}
          onChange={(e) => {
            e.stopPropagation();
            onCheckChange(id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          className="rr-item-checkbox"
        />
      )}

      <div className="rr-item-left">
        <div className="rr-icon-badge">
          <FileImageOutlined />
        </div>
        <div className="rr-item-texts">
          <div className="rr-item-date">{dateLabel}</div>
          <div className="rr-item-title">{title}</div>
        </div>
      </div>

      {visibleSharedDoctors.length > 0 && (
        <div className="rr-share-doctor-avatars rr-share-doctor-avatars--right">
          {visibleSharedDoctors.map((doctor, index) => {
            const dId = String(doctor?.id || "");
            const isRevoking = revokingDoctors.has(dId);

            const doctorStatus = (() => {
              if (typeof doctor?.isActive === "boolean") return doctor.isActive;
              if (hasExpiration && expiresAt) {
                return new Date(expiresAt).getTime() > Date.now();
              }
              return true;
            })();

            const doctorLabel =
              doctor?.name || t("recentRecords.sharedDoctor", "Doctor");
            const specialtyLabel =
              doctor?.specialty ||
              t("shareModal.defaultSpecialty", "Medical Professional");
            const avatarSrc = resolveDoctorAvatar(doctor?.avatarUrl);

            return (
              <Popover
                key={String(doctor?.id || `${doctorLabel}-${index}`)}
                trigger="click"
                placement="topLeft"
                overlayClassName="rr-share-popover"
                content={
                  <div className="rr-share-popover-content">
                    <div className="rr-share-popover-name">{doctorLabel}</div>
                    <div className="rr-share-popover-specialty">
                      {specialtyLabel}
                    </div>

                    <div className="rr-share-popover-status-row">
                      {/* Statut Activé */}
                      <span className="rr-share-popover-status-item">
                        <CheckCircleTwoTone
                          twoToneColor={doctorStatus ? "#16a34a" : "#cbd5e1"}
                        />
                        <span>{t("recentRecords.active", "Activé")}</span>
                      </span>

                      {/* Bouton Désactivé → révoque l'accès */}
                      <button
                        type="button"
                        className={`rr-share-popover-status-btn ${
                          isRevoking ? "rr-share-popover-status-btn--loading" : ""
                        }`}
                        disabled={isRevoking}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isRevoking && doctor?.id && onDeactivateDoctorShare) {
                            onDeactivateDoctorShare(id, doctor.id);
                          }
                        }}
                      >
                        <span className="rr-share-popover-status-item">
                          {isRevoking ? (
                            <Spin
                              indicator={
                                <LoadingOutlined
                                  style={{ fontSize: 14, color: "#dc2626" }}
                                  spin
                                />
                              }
                            />
                          ) : (
                            <CloseCircleTwoTone
                              twoToneColor={!doctorStatus ? "#dc2626" : "#cbd5e1"}
                            />
                          )}
                          <span>
                            {isRevoking
                              ? t("recentRecords.revoking", "Révocation...")
                              : t("recentRecords.deactivated", "Désactivé")}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                }
              >
                <button
                  type="button"
                  className={`rr-share-doctor-avatar-btn ${
                    isRevoking ? "rr-share-doctor-avatar-btn--revoking" : ""
                  }`}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t(
                    "recentRecords.viewSharedDoctor",
                    "View shared doctor details"
                  )}
                >
                  {isRevoking && (
                    <span className="rr-avatar-revoking-overlay">
                      <LoadingOutlined spin style={{ fontSize: 12 }} />
                    </span>
                  )}
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt={doctorLabel}
                      className={`rr-share-doctor-avatar ${
                        isRevoking ? "rr-share-doctor-avatar--revoking" : ""
                      }`}
                    />
                  ) : (
                    <span
                      className={`rr-share-doctor-avatar rr-share-doctor-avatar--fallback ${
                        isRevoking ? "rr-share-doctor-avatar--revoking" : ""
                      }`}
                    >
                      {(doctorLabel || "D").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </button>
              </Popover>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Skeleton
// ─────────────────────────────────────────────
function RecordSkeleton() {
  return (
    <div className="rr-item rr-skeleton" aria-hidden>
      <div className="rr-item-left">
        <div className="rr-icon-badge rr-shimmer" />
        <div className="rr-item-texts">
          <div className="rr-line rr-shimmer" style={{ width: 120 }} />
          <div className="rr-line rr-shimmer" style={{ width: 240 }} />
        </div>
      </div>
      <div className="rr-line rr-shimmer" style={{ width: 140 }} />
    </div>
  );
}

// ─────────────────────────────────────────────
// PropTypes
// ─────────────────────────────────────────────
RecentRecords.propTypes = {
  records: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dateLabel: PropTypes.string,
      title: PropTypes.string,
      hasExpiration: PropTypes.bool,
      expiresAt: PropTypes.string,
      sharedDoctors: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          name: PropTypes.string,
          specialty: PropTypes.string,
          avatarUrl: PropTypes.string,
          isActive: PropTypes.bool,
          isFromRendezvous: PropTypes.bool,
        })
      ),
      onClick: PropTypes.func,
      pdfUrl: PropTypes.string,
      fileUrl: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  onFindLab: PropTypes.func,
  onFilterChange: PropTypes.func,
  onSelect: PropTypes.func,
  onShare: PropTypes.func,
  onDelete: PropTypes.func,
  onDownload: PropTypes.func,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  bannerTextLeft: PropTypes.string,
  bannerCta: PropTypes.string,
  onShareSuccess: PropTypes.func,
  onDoctorAccessRevoked: PropTypes.func,
};
